import * as Utils from '../../commons/utils/Utils';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import CircularProgress from '@material-ui/core/CircularProgress';
import Security from '../../commons/security/Security.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import swal from 'sweetalert2';

/**
 * VEC-3905 — las solicitudes de stock de un ticket, con la edición de lo pedido.
 *
 * Es la otra mitad del Scenario 01: el popup de faltantes bloquea un repuesto que ya tiene pedido y
 * ofrece "acceder a la solicitud existente para editarla o cancelarla" — y acá es donde se accede.
 *
 * ## POR QUÉ LA EDICIÓN VIVE ACÁ Y NO EN EL POPUP
 *
 * Editar desde el popup implicaría un PUT por línea además del POST del alta: varias requests sin
 * una transacción que las cubra, y un solo cartel de error para contar qué mitad quedó aplicada.
 * Además obligaría a reimplementar allá la regla del piso (no se puede pedir menos de lo que ya te
 * entregaron). Acá hay un único lugar, con la regla en un solo lado.
 *
 * ## MUESTRA TAMBIÉN LAS CERRADAS
 *
 * El endpoint las devuelve a propósito: el historial es lo que explica qué pasó con cada repuesto.
 * Lo que no se puede es editarlas — el backend lo rechaza y acá directamente no se ofrece.
 *
 * ## CANCELAR TAMBIÉN ESTÁ ACÁ (Scenario 04)
 *
 * Dar de baja lo que falta entregar es `POST /detalles/{id}/cancelar`, con motivo obligatorio. El
 * endpoint valida `INVENTARIO_SOLICITUDES_CANCELAR` Ó `INVENTARIO_STOCK_SOLICITAR`: lo puede hacer el
 * bodeguero desde su cola y también quien pidió, que es el usuario del que habla la card.
 *
 * No borra la línea: pasa a CANCELADA, lo ya despachado queda despachado, y la combinación
 * ticket + repuesto queda libre para volver a pedirse.
 */
const ESTADO_PENDIENTE = 'PENDIENTE';

const TicketSolicitudesStock = (props) => {
	const [solicitudes, setSolicitudes] = useState([]);
	const [loading, setLoading] = useState(false);
	const intl = props.intl;

	const puedeEditar = Security.hasPermission('INVENTARIO_STOCK_SOLICITAR');
	// El endpoint valida los dos en OR: cancela el bodeguero desde su cola, y también quien pidió.
	const puedeCancelar = puedeEditar || Security.hasPermission('INVENTARIO_SOLICITUDES_CANCELAR');

	const cargar = () => {
		setLoading(true);

		// `getDataNoErrorMessage` y no `getData`: el handler genérico abriría su propio modal de
		// error ENCIMA de este, y acá el vacío ya comunica lo que hay que comunicar.
		Utils.getDataNoErrorMessage('/inventario/solicitudes?ticket=' + encodeURIComponent(props.ticket.id))
			.then((data) => setSolicitudes((data && data.solicitudes) || []))
			.catch(() => setSolicitudes([]))
			.finally(() => setLoading(false));
	};

	useEffect(cargar, [props.ticket.id]);

	/**
	 * Editar la cantidad pedida de una línea abierta (Scenarios 02 y 03).
	 *
	 * El PUT va dentro del `preConfirm` con `showLoaderOnConfirm`, igual que el alta: el modal queda
	 * abierto con el spinner y, si falla, el error sale adentro sin perder lo escrito.
	 *
	 * ⚠️ El `catch` va ACÁ ADENTRO y devuelve `false` LITERAL. En sweetalert2 7.33, sin
	 * `expectRejections`, una promesa de `preConfirm` que rechaza rechaza la promesa del modal en vez
	 * de dejarlo abierto, y la comparación que mantiene el modal abierto es estricta
	 * (`preConfirmValue === false`).
	 *
	 * ⚠️ `type:` y no `icon:`, y `input:` en vez de un HTML propio: estamos en la v7.33.
	 */
	const editar = (fila) => {
		swal({
			title: intl.formatMessage({
				id: 'ticketSolicitudesStock.editar.titulo',
				defaultMessage: 'Cambiar la cantidad solicitada',
			}),
			text: fila.item ? fila.item.descripcion : '',
			input: 'number',
			inputValue: String(fila.cantidad),
			inputAttributes: { min: String(Math.max(fila.cantidadDespachada, 1)), step: '1' },
			type: 'question',
			allowOutsideClick: false,
			showCancelButton: true,
			confirmButtonText: `<i style="margin-right:5px" class="fa fa-check-circle"></i>${intl.formatMessage({ id: 'guardar', defaultMessage: ' Guardar' })}`,
			cancelButtonText: `<i style="margin-right:5px" class="fa fa-times-circle"></i>${intl.formatMessage({ id: 'cancelar', defaultMessage: ' Cancelar' })}`,
			confirmButtonColor: '#0ABFBC',
			cancelButtonColor: '#FF3145',
			showLoaderOnConfirm: true,
			preConfirm: (valor) => {
				const cantidad = Number(valor);

				if (!Number.isInteger(cantidad) || cantidad < 1) {
					swal.showValidationMessage(
						intl.formatMessage({
							id: 'ticketSolicitudesStock.editar.cantidad_invalida',
							defaultMessage: 'La cantidad tiene que ser un número entero mayor a cero.',
						})
					);

					return false;
				}

				// El piso contra lo ya despachado NO se valida acá: lo aplica el backend, que es el
				// único que tiene el dato al momento de escribir. Adelantarlo en el front sería una
				// comodidad; reemplazarlo sería una segunda fuente de la misma regla.
				return Utils.sendDataNoErrorMessage(
					'/inventario/solicitudes/detalles/' + fila.id,
					'PUT',
					{ cantidad: cantidad }
				).then(
					() => true,
					(error) => {
						swal.showValidationMessage(mensajeDeError(intl, error, intl.formatMessage({
							id: 'ticketSolicitudesStock.editar.error',
							defaultMessage: 'No se pudo cambiar la cantidad. Volvé a intentar.',
						})));

						return false;
					}
				);
			},
		}).then((res) => {
			if (!res.value) return;

			cargar();
		});
	};

	/**
	 * Scenario 04 — cancelar lo que falta entregar de una línea pendiente.
	 *
	 * NO BORRA NADA: la línea pasa a CANCELADA y lo ya despachado queda despachado. La diferencia
	 * contra lo pedido es lo que se da de baja. Por eso cancelar una línea con entregas parciales es
	 * legítimo, y el backend no exige que esté en cero.
	 *
	 * Al cerrarse, la línea deja de bloquear: se puede volver a pedir ese repuesto para este ticket.
	 * Es la otra mitad del Scenario 04.
	 *
	 * EL MOTIVO ES OBLIGATORIO, igual que cuando cancela el bodeguero. No vive en el detalle sino en el
	 * movimiento de tipo CANCELACION, que es lo que después explica el rechazo en el historial.
	 *
	 * ⚠️ `input: 'textarea'` y `type:` en vez de `icon:`: sweetalert2 está en la 7.33.
	 */
	const cancelar = (fila) => {
		swal({
			title: intl.formatMessage({
				id: 'ticketSolicitudesStock.cancelar.titulo',
				defaultMessage: '¿Cancelar lo que falta de esta solicitud?',
			}),
			text: fila.item ? fila.item.descripcion : '',
			input: 'textarea',
			inputPlaceholder: intl.formatMessage({
				id: 'ticketSolicitudesStock.cancelar.motivo',
				defaultMessage: 'Motivo de la cancelación',
			}),
			type: 'warning',
			allowOutsideClick: false,
			showCancelButton: true,
			confirmButtonText: `<i style="margin-right:5px" class="fa fa-check-circle"></i>${intl.formatMessage({ id: 'ticketSolicitudesStock.cancelar.confirmar', defaultMessage: ' Cancelar la solicitud' })}`,
			cancelButtonText: `<i style="margin-right:5px" class="fa fa-times-circle"></i>${intl.formatMessage({ id: 'volver', defaultMessage: ' Volver' })}`,
			confirmButtonColor: '#FF3145',
			cancelButtonColor: '#bfbcbc',
			showLoaderOnConfirm: true,
			preConfirm: (motivo) => {
				// Una cadena de espacios se rechaza igual que la ausencia, como en el backend.
				if (!motivo || String(motivo).trim() === '') {
					swal.showValidationMessage(
						intl.formatMessage({
							id: 'ticketSolicitudesStock.cancelar.motivo_requerido',
							defaultMessage: 'Indicá el motivo de la cancelación.',
						})
					);

					return false;
				}

				return Utils.sendDataNoErrorMessage(
					'/inventario/solicitudes/detalles/' + fila.id + '/cancelar',
					'POST',
					{ motivo: String(motivo).trim() }
				).then(
					() => true,
					(error) => {
						swal.showValidationMessage(mensajeDeError(intl, error, intl.formatMessage({
							id: 'ticketSolicitudesStock.cancelar.error',
							defaultMessage: 'No se pudo cancelar la solicitud. Volvé a intentar.',
						})));

						return false;
					}
				);
			},
		}).then((res) => {
			if (!res.value) return;

			cargar();
		});
	};

	if (loading) return <div className="text-center"><CircularProgress /></div>;

	return (
		<React.Fragment>
			<h5>
				<FormattedMessage id="ticketSolicitudesStock.titulo" defaultMessage="Solicitudes de stock" />
			</h5>

			{solicitudes.length === 0 ? (
				<p>
					<FormattedMessage
						id="ticketSolicitudesStock.sin_solicitudes"
						defaultMessage="Este ticket no tiene solicitudes de stock."
					/>
				</p>
			) : (
				<div className="table-responsive">
					<table className="table table-sm">
						<thead>
							<tr>
								<th><FormattedMessage id="Repuesto" defaultMessage="Repuesto" /></th>
								<th><FormattedMessage id="ticketSolicitudesStock.columna_deposito" defaultMessage="Depósito" /></th>
								<th><FormattedMessage id="ticketSolicitudesStock.columna_solicitado" defaultMessage="Solicitado" /></th>
								<th><FormattedMessage id="ticketSolicitudesStock.columna_despachado" defaultMessage="Despachado" /></th>
								<th><FormattedMessage id="ticketSolicitudesStock.columna_pendiente" defaultMessage="Pendiente" /></th>
								<th><FormattedMessage id="Estado" defaultMessage="Estado" /></th>
								<th><FormattedMessage id="Fecha" defaultMessage="Fecha" /></th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							{solicitudes.map((fila) => (
								<tr key={fila.id}>
									<td>{fila.item ? fila.item.descripcion : ''}</td>
									<td>{fila.ubicacion ? fila.ubicacion.nombre : ''}</td>
									<td>{fila.cantidad}</td>
									<td>{fila.cantidadDespachada}</td>
									<td>{fila.cantidadPendiente}</td>
									<td>{traducirEstado(intl, fila.estado)}</td>
									{/* Misma presentación que la grilla de Inventario: es el mismo dato. */}
									<td>{fila.fecha ? LocaleFormat.formatDate(fila.fecha) : '-'}</td>
									<td>
										{/* Solo las abiertas se editan: el backend rechaza una cerrada, y ofrecer
										    un botón que siempre falla es peor que no tenerlo. */}
										{puedeEditar && fila.estado === ESTADO_PENDIENTE ? (
											<button
												type="button"
												className="btn btn-sm btn-link"
												onClick={() => editar(fila)}
											>
												<FormattedMessage id="editar" defaultMessage="Editar" />
											</button>
										) : null}
										{puedeCancelar && fila.estado === ESTADO_PENDIENTE ? (
											<button
												type="button"
												className="btn btn-sm btn-link text-danger"
												onClick={() => cancelar(fila)}
											>
												<FormattedMessage id="cancelar" defaultMessage="Cancelar" />
											</button>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</React.Fragment>
	);
};

/**
 * El estado, en el idioma del usuario.
 *
 * La grilla de Inventario los mostraba sin traducir (hallazgo de la demo del 14/09). Acá arrancan
 * traducidos; un estado nuevo cae a su propio valor en vez de a un vacío.
 */
const traducirEstado = (intl, estado) => {
	const claves = {
		PENDIENTE: { id: 'inventario.solicitud.estado.pendiente', defaultMessage: 'Pendiente' },
		FINALIZADA: { id: 'inventario.solicitud.estado.finalizada', defaultMessage: 'Finalizada' },
		CANCELADA: { id: 'inventario.solicitud.estado.cancelada', defaultMessage: 'Rechazada' },
	};

	return claves[estado] ? intl.formatMessage(claves[estado]) : estado;
};

/**
 * Mismo criterio que el popup: la key del backend traducida con su context, o el genérico.
 *
 * EL GENÉRICO LO PASA EL LLAMADOR. Esta función la usan editar y cancelar, y un texto único
 * describiría la operación equivocada cuando falla la otra — un corte de red al cancelar mostrando
 * "no se pudo cambiar la cantidad".
 */
const mensajeDeError = (intl, error, generico) => {
	const cuerpo = (error && error.response && error.response.data) || {};
	const key = cuerpo.mensaje;

	if (key) {
		const texto = Utils.translateBackendKey(key, cuerpo.context, intl);

		if (texto && texto !== key) return texto;
	}

	return generico;
};

export default injectIntl(TicketSolicitudesStock);
